import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useActiveOrg } from '../activeOrg';
import { useProjectsList, type ProjectListItem } from '../projects/useProjectsList';
import type { SortDescriptor } from '../../components/Table';
import {
  PAGE_SIZE,
  hasActiveFilters,
  parseSearchParams,
  stateToSearchParams,
  type FilterFacetKey,
  type ProjectListState,
  type ProjectsGroupBy,
  type ProjectsTab
} from '../models/projects';

/**
 * ViewModel for `/projects`.
 *
 * Mirrors `useDocumentsListVM`'s URL-state contract (the URL is the single
 * source of truth: hydrate on mount, sync via `history.replaceState`, restore
 * on `popstate`) but does the heavy lifting client-side. The Projects list is
 * small — an org has dozens of projects, not thousands — so rather than push
 * search / filter / sort / paging into an RPC, this VM consumes the raw rows
 * from `useProjectsList` and derives the filtered → sorted → paginated view in
 * memory. No new Supabase surface, no migration.
 */

export type ProjectsListStatus = 'loading' | 'ready' | 'empty' | 'error';

export interface ProjectStatusOption {
  id: string;
  label: string;
}

export interface ProjectLeadOption {
  id: string;
  label: string;
}

export interface ProjectsListDerived {
  /** The visible page of filtered + sorted rows. */
  rows: ReadonlyArray<ProjectListItem>;
  /** Count of rows after filtering (before paging) — drives the pager. */
  total: number;
  pageSize: number;
  statusOptions: ReadonlyArray<ProjectStatusOption>;
  leadOptions: ReadonlyArray<ProjectLeadOption>;
  loading: boolean;
  error: string | null;
  hasActiveFilters: boolean;
  /** Whether the viewer can create a project (gated on an active org). */
  canCreate: boolean;
}

export interface ProjectsListActions {
  setFilter: (
    key: FilterFacetKey,
    value: ReadonlyArray<string> | string | null
  ) => void;
  removeFilter: (key: FilterFacetKey, value?: string) => void;
  setSort: (sort: SortDescriptor) => void;
  setSearch: (q: string) => void;
  setPage: (page: number) => void;
  setTab: (tab: ProjectsTab) => void;
  setGroupBy: (groupBy: ProjectsGroupBy) => void;
  setCols: (cols: ReadonlyArray<string>) => void;
  /** Re-fetch the underlying project rows. */
  reload: () => void;
}

export interface UseProjectsListVM {
  status: ProjectsListStatus;
  state: ProjectListState;
  activeOrgId: string | null;
  derived: ProjectsListDerived;
  actions: ProjectsListActions;
}

/** Initial-state seam. The VM hydrates from the **route-scoped** query
 *  (react-router `useSearchParams`, which under HashRouter targets the URL
 *  fragment's `?…`). The host page may pass a partial override (e.g. a
 *  localStorage-restored `tab`) applied once on top of the parsed query. */
export type ProjectsListInitialOverrides = Partial<ProjectListState>;

/** Numeric progress fraction for the `progress` sort axis. Projects with no
 *  counted work items sort below any project that has progress. */
function progressFraction(item: ProjectListItem): number {
  return item.totalCount > 0 ? item.doneCount / item.totalCount : -1;
}

function compareItems(
  a: ProjectListItem,
  b: ProjectListItem,
  sort: SortDescriptor
): number {
  const dir = sort.direction === 'desc' ? -1 : 1;
  switch (sort.columnKey) {
    case 'name':
      return a.project.name.localeCompare(b.project.name) * dir;
    case 'status':
      return a.project.status.localeCompare(b.project.status) * dir;
    case 'progress':
      return (progressFraction(a) - progressFraction(b)) * dir;
    case 'target_date': {
      // Nulls always sort last regardless of direction.
      const at = a.project.target_date;
      const bt = b.project.target_date;
      if (!at && !bt) return 0;
      if (!at) return 1;
      if (!bt) return -1;
      return at.localeCompare(bt) * dir;
    }
    case 'created_at':
    default:
      return (a.project.created_at ?? '').localeCompare(
        b.project.created_at ?? ''
      ) * dir;
  }
}

export function useProjectsListVM(
  overrides?: ProjectsListInitialOverrides
): UseProjectsListVM {
  const { activeOrgId } = useActiveOrg();
  const { state: listState, items, error, reload } = useProjectsList(activeOrgId);

  // The URL query is owned by react-router (`useSearchParams`), which under the
  // app's HashRouter reads/writes the route-scoped fragment query (`#/projects?…`)
  // — NOT `window.location.search`, the single query string shared across every
  // route. Reading the shared search was the cross-page leak: the Documents
  // page's `?cols=` survived navigation and stranded the /projects gallery+table.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const initialState = useMemo<ProjectListState>(() => {
    const base = parseSearchParams(searchParams);
    return overrides ? { ...base, ...overrides } : base;
    // Evaluated once on mount; the VM owns URL state thereafter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [state, setState] = useState<ProjectListState>(initialState);

  // Mirror state → the route-scoped URL query. Equality-gated so it's a no-op
  // when the URL already matches (incl. StrictMode's double effect run) and so
  // it never fights an external back/forward navigation.
  useEffect(() => {
    const desired = stateToSearchParams(state);
    const current = stateToSearchParams(parseSearchParams(searchParamsRef.current));
    if (desired.toString() !== current.toString()) {
      setSearchParams(desired, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, setSearchParams]);

  // ----- Status mapping --------------------------------------------------
  const status: ProjectsListStatus =
    listState === 'no-org'
      ? 'empty'
      : listState === 'error'
        ? 'error'
        : listState === 'ready'
          ? 'ready'
          : 'loading';

  // ----- Facet option pools (distinct over all loaded rows) --------------
  const statusOptions = useMemo<ReadonlyArray<ProjectStatusOption>>(() => {
    const seen = new Set<string>();
    for (const it of items) {
      const s = it.project.status?.trim();
      if (s) seen.add(s);
    }
    return Array.from(seen)
      .sort((a, b) => a.localeCompare(b))
      .map((s) => ({ id: s, label: s }));
  }, [items]);

  const leadOptions = useMemo<ReadonlyArray<ProjectLeadOption>>(() => {
    const byId = new Map<string, string>();
    for (const it of items) {
      const id = it.project.lead_id;
      if (id && !byId.has(id)) byId.set(id, it.leadName ?? 'Unknown');
    }
    return Array.from(byId.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  // ----- Filter → sort → page (client-side) ------------------------------
  const filtered = useMemo<ReadonlyArray<ProjectListItem>>(() => {
    const q = state.q.trim().toLowerCase();
    const statusSet = new Set(state.status);
    const leadSet = new Set(state.lead);
    const out = items.filter((it) => {
      if (statusSet.size > 0 && !statusSet.has(it.project.status)) return false;
      if (leadSet.size > 0 && !(it.project.lead_id && leadSet.has(it.project.lead_id)))
        return false;
      if (q) {
        const hay = `${it.project.name} ${it.project.description ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return [...out].sort((a, b) => compareItems(a, b, state.sort));
  }, [items, state.q, state.status, state.lead, state.sort]);

  const total = filtered.length;

  const rows = useMemo<ReadonlyArray<ProjectListItem>>(() => {
    const start = (state.page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, state.page]);

  // ----- Actions: every mutator updates state; the effect above mirrors it
  // ----- to the route-scoped URL query. -----------------------------------
  const setFilter = useCallback<ProjectsListActions['setFilter']>((key, value) => {
    setState((prev) => {
      const arr = Array.isArray(value)
        ? (value as ReadonlyArray<string>)
        : value
          ? [value as string]
          : [];
      const next: ProjectListState = { ...prev, [key]: arr, page: 1 };
      return next;
    });
  }, []);

  const removeFilter = useCallback<ProjectsListActions['removeFilter']>(
    (key, value) => {
      setState((prev) => {
        const list = prev[key];
        const nextList =
          value === undefined ? [] : list.filter((v) => v !== value);
        const next: ProjectListState = { ...prev, [key]: nextList, page: 1 };
        return next;
      });
    },
    []
  );

  const setSort = useCallback<ProjectsListActions['setSort']>((sort) => {
    setState((prev) => {
      const next = { ...prev, sort, page: 1 };
      return next;
    });
  }, []);

  const setSearch = useCallback<ProjectsListActions['setSearch']>((q) => {
    setState((prev) => {
      const next = { ...prev, q, page: 1 };
      return next;
    });
  }, []);

  const setPage = useCallback<ProjectsListActions['setPage']>((page) => {
    setState((prev) => {
      if (!Number.isFinite(page) || page < 1) return prev;
      const next = { ...prev, page };
      return next;
    });
  }, []);

  const setTab = useCallback<ProjectsListActions['setTab']>((tab) => {
    setState((prev) => {
      if (prev.tab === tab) return prev;
      const next = { ...prev, tab };
      return next;
    });
  }, []);

  const setGroupBy = useCallback<ProjectsListActions['setGroupBy']>((groupBy) => {
    setState((prev) => {
      if (prev.groupBy === groupBy) return prev;
      const next = { ...prev, groupBy };
      return next;
    });
  }, []);

  const setCols = useCallback<ProjectsListActions['setCols']>((cols) => {
    setState((prev) => {
      const next = { ...prev, cols };
      return next;
    });
  }, []);

  const actions = useMemo<ProjectsListActions>(
    () => ({
      setFilter,
      removeFilter,
      setSort,
      setSearch,
      setPage,
      setTab,
      setGroupBy,
      setCols,
      reload
    }),
    [setFilter, removeFilter, setSort, setSearch, setPage, setTab, setGroupBy, setCols, reload]
  );

  const derived = useMemo<ProjectsListDerived>(
    () => ({
      rows,
      total,
      pageSize: PAGE_SIZE,
      statusOptions,
      leadOptions,
      loading: listState === 'loading',
      error,
      hasActiveFilters: hasActiveFilters(state),
      canCreate: !!activeOrgId
    }),
    [rows, total, statusOptions, leadOptions, listState, error, state, activeOrgId]
  );

  return { status, state, activeOrgId, derived, actions };
}
